import { useConfirm } from '@estac1/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../../lib/api.js';
import type { PendingModuleToggle } from './lot-module-toggles.js';
import type { Client, Lot, PricingSettings, StatusFilter } from './types.js';
import { useClientMutations } from './use-client-mutations.js';
import { mrrForLot } from './utils.js';

export const useClientsScreen = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [extending, setExtending] = useState<{ lotId: string; lotName: string } | null>(null);
  const [extraDays, setExtraDays] = useState(7);
  const archiveConfirm = useConfirm();
  const unarchiveConfirm = useConfirm();
  const moduleToggleConfirm = useConfirm();
  const resetCheckoutConfirm = useConfirm();
  const [pendingArchive, setPendingArchive] = useState<Lot | null>(null);
  const [pendingUnarchive, setPendingUnarchive] = useState<Lot | null>(null);
  const [pendingResetCheckout, setPendingResetCheckout] = useState<Lot | null>(null);
  const [pendingModuleToggle, setPendingModuleToggle] = useState<PendingModuleToggle | null>(null);

  const clients = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => api.request<{ clients: Client[] }>('/v1/admin/clients'),
  });

  const pricing = useQuery({
    queryKey: ['admin-settings-pricing'],
    queryFn: () => api.request<PricingSettings>('/v1/admin/settings/pricing'),
  });

  const { extendTrial, archive, unarchive, resetCheckout, toggleModule } =
    useClientMutations(setExtending);

  const allClients = clients.data?.clients ?? [];

  const kpis = useMemo(() => {
    let active = 0;
    let trial = 0;
    let pastDue = 0;
    let mrrCents = 0;
    for (const c of allClients) {
      for (const lot of c.lots) {
        if (lot.subscriptionStatus === 'active') active += 1;
        else if (lot.subscriptionStatus === 'trial') trial += 1;
        else if (lot.subscriptionStatus === 'past_due') pastDue += 1;
        if (lot.subscriptionStatus === 'active') {
          mrrCents += mrrForLot(lot, pricing.data) ?? 0;
        }
      }
    }
    return { active, trial, pastDue, mrrCents };
  }, [allClients, pricing.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allClients
      .map((c) => {
        const lots = c.lots.filter((lot) => {
          if (statusFilter === 'all') return lot.archivedAt === null;
          if (statusFilter === 'archived') return lot.archivedAt !== null;
          return lot.archivedAt === null && lot.subscriptionStatus === statusFilter;
        });
        return { ...c, lots };
      })
      .filter((c) => {
        if (c.lots.length === 0 && statusFilter !== 'all') return false;
        if (c.lots.length === 0 && statusFilter === 'all' && c.archivedAt === null) return false;
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.lots.some((l) => l.name.toLowerCase().includes(q))
        );
      });
  }, [allClients, search, statusFilter]);

  return {
    clients,
    pricing,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    extending,
    setExtending,
    extraDays,
    setExtraDays,
    archiveConfirm,
    unarchiveConfirm,
    moduleToggleConfirm,
    resetCheckoutConfirm,
    pendingArchive,
    setPendingArchive,
    pendingUnarchive,
    setPendingUnarchive,
    pendingResetCheckout,
    setPendingResetCheckout,
    pendingModuleToggle,
    setPendingModuleToggle,
    extendTrial,
    archive,
    unarchive,
    resetCheckout,
    toggleModule,
    kpis,
    filtered,
  };
};
